import React from 'react'
import { Button, Fieldset, Group, LoadingOverlay, Modal, NumberInput, Radio, Slider, Stack, Switch, Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import { INITIAL_VALUES } from '../../../../constants/pdf-report-download'
import { PDFOptionsForm, ReportDownloadFileType } from '../../../../lib/types/ReportDownload.type'
import { HeaderFooterOption } from './HeaderFooterOption'
import { genPdfOptionsString } from './helpers'

type PDFOptionsModalProps = {
  opened: boolean
  onClose: () => void
  downloadPending: boolean
  onSubmit: (fileType: ReportDownloadFileType, pdfOptions: { optionsJson: PDFOptionsForm, optionsString: string }) => void
}

export const PDFOptionsModal = ({ opened, onClose, downloadPending, onSubmit }: PDFOptionsModalProps) => {
  const form = useForm<PDFOptionsForm>({
    mode: 'controlled',
    initialValues: INITIAL_VALUES
  });

  const handleSubmit = (values: PDFOptionsForm) => {
    const optionsString = genPdfOptionsString(values)
    onSubmit('pdf', { optionsJson: values, optionsString })
  }

  return (
    <Modal title='PDF Options' size='auto' opened={opened} onClose={onClose}>
      <LoadingOverlay visible={downloadPending} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
      <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
        <Stack>
          <Fieldset legend="Paper">
            <Radio.Group label='Paper Size' mb={15} {...form.getInputProps('paper_size')} key={form.key('paper_size')}>
              <Group>
                <Radio value='USLetter' label='US Letter' />
                <Radio value='Legal' label='Legal' />
                <Radio value='A4' label='A4' />
              </Group>
            </Radio.Group>
            <Text size='sm'>Font Size</Text>
            <Slider
              defaultValue={8}
              {...form.getInputProps('font_size')}
              key={form.key('font_size')}
              w='50%'
              mb={20}
              min={6}
              max={12}
              label={(value) => value.toFixed(1)}
              step={0.5}
              marks={[
                { value: 6, label: '6' },
                { value: 8, label: '8' },
                { value: 10, label: '10' },
                { value: 12, label: '12' },
              ]}
            />
            <Radio.Group label='Orientation' mb={15} {...form.getInputProps('orientation')} key={form.key('orientation')}>
              <Group>
                <Radio defaultChecked value='portrait' label='Portrait' />
                <Radio value='landscape' label='Landscape' />
              </Group>
            </Radio.Group>
          </Fieldset>
          <Fieldset legend="Header/Footer">
            <Radio.Group label='Display Logo' mb={15} {...form.getInputProps('header_has_logo')} key={form.key('header_has_logo')}>
              <Group>
                <Radio defaultChecked value='header_has_logo' label='Every page' />
                <Radio value='first_page_only' label='First page only' />
                <Radio value='no_logo' label='No logo' />
              </Group>
            </Radio.Group>
            <HeaderFooterOption label='Header 1' form={form} field='header1' />
            <HeaderFooterOption label='Header 2' form={form} field='header2' />
            <HeaderFooterOption label='Footer' form={form} field='footer' />
            <Group mt={15} align='flex-end'>
              <Switch checked={form.getValues().footer_date} {...form.getInputProps('footer_date')} key={form.key('footer_date')} label='Date' />
              <Radio.Group {...form.getInputProps('date_position')} key={form.key('date_position')}>
                <Group>
                  <Radio disabled={!form.getValues().footer_date} value='Left' label='Left' />
                  <Radio disabled={!form.getValues().footer_date} value='Center' label='Center' />
                  <Radio disabled={!form.getValues().footer_date} value='Right' label='Right' />
                </Group>
              </Radio.Group>
            </Group>
            <Group mt={15} align='flex-end'>
              <Switch checked={form.getValues().footer_page_numbers} {...form.getInputProps('footer_page_numbers')} key={form.key('footer_page_numbers')} label='Page Number' />
              <Radio.Group {...form.getInputProps('page_number_position')} key={form.key('page_number_position')}>
                <Group>
                  <Radio disabled={!form.getValues().footer_page_numbers} value='Left' label='Left' />
                  <Radio disabled={!form.getValues().footer_page_numbers} value='Center' label='Center' />
                  <Radio disabled={!form.getValues().footer_page_numbers} value='Right' label='Right' />
                </Group>
              </Radio.Group>
            </Group>
          </Fieldset>
          <Fieldset legend='Margin'>
            <Group>
              <NumberInput label='Top' suffix=' inches' step={.1} {...form.getInputProps('top_margin')} key={form.key('top_margin')} />
              <NumberInput label='Bottom' suffix=' inches' step={.1} {...form.getInputProps('bottom_margin')} key={form.key('bottom_margin')} />
              <NumberInput label='Left' suffix=' inches' step={.1} {...form.getInputProps('left_margin')} key={form.key('left_margin')} />
              <NumberInput label='Right' suffix=' inches' step={.1} {...form.getInputProps('right_margin')} key={form.key('right_margin')} />
            </Group>
          </Fieldset>
          <Group mt={25} justify='flex-end'>
            <Button variant='outline'onClick={onClose}>Cancel</Button>
            <Button type='submit' disabled={downloadPending}>Download</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
